// Small hook to manage a position:fixed body scroll lock with ownership semantics.
// The first owner to request a lock saves the scroll position and becomes the owner.
// Only the owner will restore the saved position and remove the lock on unlock/cleanup.
// targetSelector: optional CSS selector string to lock (defaults to 'body')
import { useEffect } from 'react';

// Stack entry: { owner, pos, selector }
function readStack() {
    try {
        const raw = document.body.dataset.scrollStack;
        return raw ? JSON.parse(raw) : [];
    } catch (_e) {
        return [];
    }
}

function writeStack(stack) {
    try {
        if (!stack || stack.length === 0) {
            delete document.body.dataset.scrollStack;
        } else {
            document.body.dataset.scrollStack = JSON.stringify(stack);
        }
    } catch (_e) {
        // ignore
    }
}

export default function useBodyScrollLock(
    owner,
    enabled,
    targetSelector = 'body',
) {
    useEffect(() => {
        if (typeof document === 'undefined') return;

        const target = document.querySelector(targetSelector) || document.body;

        // single helper to measure native scrollbar width
        const getScrollbarWidth = () => {
            try {
                const outer = document.createElement('div');
                outer.style.visibility = 'hidden';
                outer.style.width = '100px';
                outer.style.msOverflowStyle = 'scrollbar';
                outer.style.position = 'absolute';
                outer.style.top = '-9999px';
                outer.style.overflow = 'scroll';
                document.body.appendChild(outer);
                const inner = document.createElement('div');
                inner.style.width = '100%';
                outer.appendChild(inner);
                const sw = outer.offsetWidth - inner.clientWidth;
                document.body.removeChild(outer);
                return Math.max(0, Math.round(sw));
            } catch (_e) {
                return 0;
            }
        };

        function applyGutterToElement(el) {
            try {
                el.classList.add('reserve-scrollbar-gutter');
            } catch (_e) {
                // ignore
            }
            // Prefer native CSS gutter if supported; otherwise add JS padding fallback
            try {
                if (
                    typeof CSS !== 'undefined' &&
                    typeof CSS.supports === 'function' &&
                    CSS.supports('scrollbar-gutter', 'stable')
                ) {
                    try {
                        el.classList.add('reserve-scrollbar-gutter');
                    } catch (_e) {
                        /* ignore */
                    }
                    return;
                }
            } catch (_e) {
                // ignore and fall back to JS measurement
            }

            try {
                // Only run JS padding fallback when native CSS gutter isn't available
                if (
                    !(
                        typeof CSS !== 'undefined' &&
                        typeof CSS.supports === 'function' &&
                        CSS.supports('scrollbar-gutter', 'stable')
                    )
                ) {
                    let sw = getScrollbarWidth();
                    // clamp to a sensible max to avoid oversized gutters on some platforms
                    sw = Math.min(sw, 12);
                    if (sw > 0) {
                        // preserve previous inline paddingRight so we can restore it
                        if (
                            typeof el.dataset.prevPaddingRight === 'undefined'
                        ) {
                            el.dataset.prevPaddingRight =
                                el.style.paddingRight || '';
                        }
                        // compute existing computed padding and add scrollbar width to it
                        const computed =
                            parseFloat(
                                window.getComputedStyle(el).paddingRight,
                            ) || 0;
                        el.style.paddingRight = `${computed + sw}px`;
                    }
                }
            } catch (_e) {
                // ignore
            }
        }

        function removeGutterFromElement(el) {
            try {
                el.classList.remove('reserve-scrollbar-gutter');
            } catch (_e) {
                // ignore
            }
            try {
                if (typeof el.dataset.prevPaddingRight !== 'undefined') {
                    el.style.paddingRight = el.dataset.prevPaddingRight || '';
                    delete el.dataset.prevPaddingRight;
                }
            } catch (_e) {
                // ignore
            }
        }

        // enable
        if (enabled) {
            const stack = readStack();
            const selectorLockCountBefore = stack.filter(
                (s) => s.selector === targetSelector,
            ).length;
            const pos =
                target === document.body
                    ? window.scrollY || 0
                    : target.scrollTop || 0;
            stack.push({ owner, pos, selector: targetSelector });
            writeStack(stack);

            if (target !== document.body) {
                // reserve gutter on target element when first lock for this selector
                if (selectorLockCountBefore === 0) applyGutterToElement(target);
                target.style.overflowY = 'hidden';
            } else {
                // body lock
                document.body.style.position = 'fixed';
                document.body.style.top = `-${pos}px`;
                document.body.style.left = '0';
                document.body.style.right = '0';

                if (selectorLockCountBefore === 0) {
                    // prefer applying gutter to root
                    try {
                        document.documentElement.classList.add(
                            'reserve-scrollbar-gutter',
                        );
                    } catch (_e) {
                        // ignore
                    }
                    try {
                        // Only apply JS padding fallback when native CSS gutter isn't available
                        if (
                            !(
                                typeof CSS !== 'undefined' &&
                                typeof CSS.supports === 'function' &&
                                CSS.supports('scrollbar-gutter', 'stable')
                            )
                        ) {
                            let sw = getScrollbarWidth();
                            sw = Math.min(sw, 12);
                            if (sw > 0) {
                                if (
                                    typeof document.documentElement.dataset
                                        .prevPaddingRight === 'undefined'
                                ) {
                                    document.documentElement.dataset.prevPaddingRight =
                                        document.documentElement.style
                                            .paddingRight || '';
                                }
                                const computed =
                                    parseFloat(
                                        window.getComputedStyle(
                                            document.documentElement,
                                        ).paddingRight,
                                    ) || 0;
                                document.documentElement.style.paddingRight = `${computed + sw}px`;
                            }
                        }
                    } catch (_e) {
                        // ignore
                    }
                }
            }
            document.body.classList.add('sidebar-scroll-lock');
        } else {
            // disable: pop the owner's last occurrence
            const stack = readStack();
            let removed = null;
            for (let i = stack.length - 1; i >= 0; i--) {
                if (
                    stack[i].owner === owner &&
                    stack[i].selector === targetSelector
                ) {
                    removed = stack.splice(i, 1)[0];
                    break;
                }
            }
            writeStack(stack);

            if (removed) {
                // find last remaining entry for this selector
                const lastForSelector = (() => {
                    for (let i = stack.length - 1; i >= 0; i--) {
                        if (stack[i].selector === targetSelector)
                            return stack[i];
                    }
                    return null;
                })();

                if (lastForSelector) {
                    // keep lock active, restore scroll to previous saved pos for this selector
                    if (target !== document.body) {
                        target.style.overflowY = 'hidden';
                        try {
                            if (
                                typeof lastForSelector.pos === 'number' &&
                                !Number.isNaN(lastForSelector.pos)
                            )
                                target.scrollTop = lastForSelector.pos;
                        } catch (_e) {
                            // ignore
                        }
                    } else {
                        document.body.style.top = `-${lastForSelector.pos || 0}px`;
                    }
                } else {
                    // fully unlock this selector
                    if (target !== document.body) {
                        try {
                            target.style.overflowY = 'auto';
                            if (
                                typeof removed.pos === 'number' &&
                                !Number.isNaN(removed.pos)
                            )
                                target.scrollTop = removed.pos;
                        } catch (_e) {
                            // ignore
                        }
                        removeGutterFromElement(target);
                    } else {
                        // body fully unlock
                        document.body.classList.remove('sidebar-scroll-lock');
                        document.body.style.position = '';
                        document.body.style.top = '';
                        document.body.style.left = '';
                        document.body.style.right = '';
                        try {
                            // restore any root padding we set
                            if (
                                typeof document.documentElement.dataset
                                    .prevPaddingRight !== 'undefined'
                            ) {
                                document.documentElement.style.paddingRight =
                                    document.documentElement.dataset
                                        .prevPaddingRight || '';
                                delete document.documentElement.dataset
                                    .prevPaddingRight;
                            }
                        } catch (_e) {
                            // ignore
                        }
                        try {
                            document.documentElement.classList.remove(
                                'reserve-scrollbar-gutter',
                            );
                        } catch (_e) {
                            // ignore
                        }
                        if (
                            typeof removed.pos === 'number' &&
                            !Number.isNaN(removed.pos)
                        )
                            window.scrollTo(0, removed.pos);
                    }
                }
            }
        }

        return () => {
            // cleanup on unmount: remove owner's last occurrence for this selector
            if (typeof document === 'undefined') return;
            const stack = readStack();
            let removed = null;
            for (let i = stack.length - 1; i >= 0; i--) {
                if (
                    stack[i].owner === owner &&
                    stack[i].selector === targetSelector
                ) {
                    removed = stack.splice(i, 1)[0];
                    break;
                }
            }
            writeStack(stack);

            if (removed) {
                const target =
                    document.querySelector(removed.selector) || document.body;
                const lastForSelector = (() => {
                    for (let i = stack.length - 1; i >= 0; i--) {
                        if (stack[i].selector === removed.selector)
                            return stack[i];
                    }
                    return null;
                })();

                if (lastForSelector) {
                    if (target !== document.body) {
                        target.style.overflowY = 'hidden';
                        try {
                            if (
                                typeof lastForSelector.pos === 'number' &&
                                !Number.isNaN(lastForSelector.pos)
                            )
                                target.scrollTop = lastForSelector.pos;
                        } catch (_e) {
                            // ignore
                        }
                    } else {
                        document.body.style.top = `-${lastForSelector.pos || 0}px`;
                    }
                } else {
                    if (target !== document.body) {
                        try {
                            target.style.overflowY = 'auto';
                            if (
                                typeof removed.pos === 'number' &&
                                !Number.isNaN(removed.pos)
                            )
                                target.scrollTop = removed.pos;
                        } catch (_e) {
                            // ignore
                        }
                        removeGutterFromElement(target);
                    } else {
                        document.body.classList.remove('sidebar-scroll-lock');
                        document.body.style.position = '';
                        document.body.style.top = '';
                        document.body.style.left = '';
                        document.body.style.right = '';
                        try {
                            if (
                                typeof document.documentElement.dataset
                                    .prevPaddingRight !== 'undefined'
                            ) {
                                document.documentElement.style.paddingRight =
                                    document.documentElement.dataset
                                        .prevPaddingRight || '';
                                delete document.documentElement.dataset
                                    .prevPaddingRight;
                            }
                        } catch (_e) {
                            // ignore
                        }
                        try {
                            document.documentElement.classList.remove(
                                'reserve-scrollbar-gutter',
                            );
                        } catch (_e) {
                            // ignore
                        }
                        if (
                            typeof removed.pos === 'number' &&
                            !Number.isNaN(removed.pos)
                        )
                            window.scrollTo(0, removed.pos);
                    }
                }
            }
        };
    }, [enabled, owner, targetSelector]);
}
